import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, MouseEvent as ReactMouseEvent } from 'react'
import type { Line, Role } from '../api/types'
import { formatTime, formatTimeShort } from '../lib/format'

/**
 * Сегмент предпросмотра: и автоматическая разметка (docs/DUBBING.md),
 * и автоматический разбор (docs/ANALYSIS.md) кладутся на одну дорожку.
 * `label` необязателен — у детектора тишины текста нет.
 */
export type PreviewSegment = { start: number; end: number; label?: string }

export type TimelineProps = {
  duration: number
  lines: Line[]
  roles: Role[]
  currentTime: number
  selectedLineId: string | null
  takeCountByLine: Record<string, number>
  onSeek: (time: number) => void
  onSelectLine: (lineId: string) => void
  /** Предпросмотр автоматической разметки — отдельной дорожкой поверх реплик. */
  previewSegments?: readonly PreviewSegment[]
  /** Подпись дорожки предпросмотра: «разметка» или «разбор». */
  previewCaption?: string
  /**
   * Протяжка мышью по свободному месту заводит реплику с этими границами
   * (docs/SEGMENTATION.md, «Объединение и создание реплик») — самый быстрый
   * способ отметить крик, который не поймала автоматика. Не задан — таймлайн
   * ведёт себя как раньше и только перематывает.
   */
  onCreateRange?: (start: number, end: number) => void
}

type DragRange = { from: number; to: number }

const TICK_STEPS = [0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600]
const LANE_HEIGHT = 30
const NO_ROLE_COLOR = '#5a6577'
/**
 * Ниже этих порогов протяжка считается промахом мыши, а не намерением:
 * событие достаётся обычному клику, и таймлайн просто перематывает.
 */
const MIN_DRAG_PX = 6
const MIN_DRAG_SECONDS = 0.1

function pickTickStep(pxPerSecond: number): number {
  for (const step of TICK_STEPS) {
    if (step * pxPerSecond >= 64) return step
  }
  return TICK_STEPS[TICK_STEPS.length - 1] as number
}

function textColorFor(hex: string): string {
  const clean = hex.replace('#', '')
  if (clean.length !== 6) return '#0c0f14'
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#0c0f14' : '#ffffff'
}

function packLanes(lines: Line[]): Array<Line[]> {
  const sorted = [...lines].sort((a, b) => a.start - b.start)
  const lanes: Array<Line[]> = []
  for (const line of sorted) {
    let placed = false
    for (const lane of lanes) {
      const last = lane[lane.length - 1]
      if (last && last.end <= line.start + 0.001) {
        lane.push(line)
        placed = true
        break
      }
    }
    if (!placed) lanes.push([line])
  }
  return lanes
}

export function Timeline({
  duration,
  lines,
  roles,
  currentTime,
  selectedLineId,
  takeCountByLine,
  onSeek,
  onSelectLine,
  previewSegments,
  previewCaption = 'разметка',
  onCreateRange,
}: TimelineProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [viewportWidth, setViewportWidth] = useState(800)
  const [zoom, setZoom] = useState(1)

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setViewportWidth(Math.max(200, entry.contentRect.width))
    })
    observer.observe(element)
    setViewportWidth(Math.max(200, element.clientWidth))
    return () => observer.disconnect()
  }, [])

  const safeDuration = duration > 0 ? duration : 60
  const contentWidth = Math.max(viewportWidth, viewportWidth * zoom)
  const pxPerSecond = contentWidth / safeDuration
  const lanes = useMemo(() => packLanes(lines), [lines])
  const roleById = useMemo(() => new Map(roles.map((role) => [role.id, role])), [roles])
  const tickStep = pickTickStep(pxPerSecond)

  const ticks: number[] = []
  for (let t = 0; t <= safeDuration + 0.001; t += tickStep) ticks.push(t)

  // Держим playhead в поле зрения при проигрывании.
  useEffect(() => {
    const element = scrollRef.current
    if (!element || zoom <= 1) return
    const x = currentTime * pxPerSecond
    const left = element.scrollLeft
    const right = left + element.clientWidth
    if (x < left + 40 || x > right - 40) {
      element.scrollLeft = Math.max(0, x - element.clientWidth / 2)
    }
  }, [currentTime, pxPerSecond, zoom])

  // ------------------------------------------------- перемотка и протяжка

  // Свежие значения для оконных обработчиков: подписка живёт дольше рендера,
  // а масштаб между рендерами меняется.
  const pxPerSecondRef = useRef(pxPerSecond)
  pxPerSecondRef.current = pxPerSecond
  const durationRef = useRef(safeDuration)
  durationRef.current = safeDuration
  const createRangeRef = useRef(onCreateRange)
  createRangeRef.current = onCreateRange

  const [drag, setDrag] = useState<DragRange | null>(null)
  const dragRef = useRef<DragRange | null>(null)
  /** Протяжка заканчивается click'ом по тому же месту — его надо съесть. */
  const suppressClickRef = useRef(false)

  const timeAt = useCallback((clientX: number): number => {
    const element = scrollRef.current
    if (!element) return 0
    const rect = element.getBoundingClientRect()
    const x = clientX - rect.left + element.scrollLeft
    return Math.min(durationRef.current, Math.max(0, x / pxPerSecondRef.current))
  }, [])

  const handleBackgroundMouseDown = (event: ReactMouseEvent<HTMLDivElement>): void => {
    // Нажатие на реплику сюда не доходит: блоки гасят mousedown, чтобы
    // протяжка начиналась только на свободном месте.
    suppressClickRef.current = false
    if (!onCreateRange || event.button !== 0) return
    event.preventDefault()
    const time = timeAt(event.clientX)
    const range: DragRange = { from: time, to: time }
    dragRef.current = range
    setDrag(range)
  }

  const dragging = drag !== null

  // Обработчики висят на window только пока тянут и снимаются в том числе при
  // размонтировании — иначе протяжка «залипнет» после ухода со страницы.
  useEffect(() => {
    if (!dragging) return
    const move = (event: MouseEvent): void => {
      const current = dragRef.current
      if (!current) return
      const next: DragRange = { from: current.from, to: timeAt(event.clientX) }
      dragRef.current = next
      setDrag(next)
    }
    const finish = (): void => {
      const current = dragRef.current
      dragRef.current = null
      setDrag(null)
      if (!current) return
      const start = Math.min(current.from, current.to)
      const end = Math.max(current.from, current.to)
      const px = (end - start) * pxPerSecondRef.current
      // Слишком короткая протяжка — это обычный клик: пусть перематывает.
      if (px < MIN_DRAG_PX || end - start < MIN_DRAG_SECONDS) return
      suppressClickRef.current = true
      createRangeRef.current?.(start, end)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', finish)
    return () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', finish)
    }
  }, [dragging, timeAt])

  const handleBackgroundClick = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }
    const element = scrollRef.current
    if (!element) return
    const rect = element.getBoundingClientRect()
    const x = event.clientX - rect.left + element.scrollLeft
    onSeek(Math.min(safeDuration, Math.max(0, x / pxPerSecond)))
  }

  const dragStart = drag ? Math.min(drag.from, drag.to) : 0
  const dragEnd = drag ? Math.max(drag.from, drag.to) : 0

  const lanesHeight = Math.max(LANE_HEIGHT, lanes.length * LANE_HEIGHT)

  return (
    <div className="stack">
      <div className="row">
        <h2>Таймлайн</h2>
        <span className="spacer" />
        <span className="faint">
          {lines.length} {lines.length === 1 ? 'реплика' : 'реплик'}
        </span>
        <button
          type="button"
          className="ghost small"
          onClick={() => setZoom((z) => Math.max(1, z / 1.6))}
          disabled={zoom <= 1}
          title="Отдалить"
        >
          −
        </button>
        <span className="faint mono" style={{ minWidth: 42, textAlign: 'center' }}>
          {zoom.toFixed(1)}×
        </span>
        <button
          type="button"
          className="ghost small"
          onClick={() => setZoom((z) => Math.min(40, z * 1.6))}
          title="Приблизить"
        >
          +
        </button>
        <button type="button" className="ghost small" onClick={() => setZoom(1)}>
          Вписать
        </button>
      </div>

      {onCreateRange ? (
        <span className="faint">
          Клик — перемотка, протяжка по свободному месту — новая реплика с этими границами.
        </span>
      ) : null}

      <div className="timeline-wrap" ref={scrollRef}>
        <div
          className={`timeline${dragging ? ' dragging' : ''}`}
          style={{ width: contentWidth }}
          onClick={handleBackgroundClick}
          onMouseDown={handleBackgroundMouseDown}
        >
          <div className="timeline-ruler">
            {ticks.map((tick) => (
              <div key={tick} className="timeline-tick" style={{ left: tick * pxPerSecond }}>
                {formatTimeShort(tick)}
              </div>
            ))}
          </div>
          {previewSegments && previewSegments.length > 0 ? (
            <div className="timeline-preview-lane" style={{ height: LANE_HEIGHT }}>
              {previewSegments.map((segment, index) => (
                <div
                  key={`${segment.start}-${index}`}
                  className="timeline-preview-block"
                  style={{
                    left: segment.start * pxPerSecond,
                    width: Math.max(3, (segment.end - segment.start) * pxPerSecond),
                  }}
                  title={`Сегмент ${index + 1}: ${formatTime(segment.start)} → ${formatTime(
                    segment.end,
                  )}${segment.label ? ` · ${segment.label}` : ''}`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation()
                    onSeek(segment.start)
                  }}
                />
              ))}
              <span className="timeline-preview-caption">
                {previewCaption} · {previewSegments.length}
              </span>
            </div>
          ) : null}
          <div className="timeline-lanes" style={{ height: lanesHeight }}>
            {lanes.length === 0 ? (
              <div className="timeline-empty">
                {onCreateRange
                  ? 'Реплик пока нет — протяните мышью по таймлайну'
                  : 'Реплик пока нет — добавьте первую'}
              </div>
            ) : (
              lanes.map((lane, laneIndex) => (
                <div key={laneIndex} className="timeline-lane" style={{ height: LANE_HEIGHT }}>
                  {lane.map((line) => {
                    const role = roleById.get(line.roleId)
                    const color = role?.color ?? NO_ROLE_COLOR
                    const width = Math.max(4, (line.end - line.start) * pxPerSecond)
                    const takes = takeCountByLine[line.id] ?? 0
                    return (
                      <div
                        key={line.id}
                        className={`timeline-block${line.id === selectedLineId ? ' selected' : ''}`}
                        style={{
                          left: line.start * pxPerSecond,
                          width,
                          background: color,
                          color: textColorFor(color),
                        }}
                        title={`${role?.name ?? 'Без роли'} · ${line.text || 'без текста'}${
                          takes > 0 ? ` · дублей: ${takes}` : ''
                        }`}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation()
                          onSelectLine(line.id)
                          onSeek(line.start)
                        }}
                      >
                        {takes > 0 ? '● ' : ''}
                        {line.text || role?.name || 'реплика'}
                      </div>
                    )
                  })}
                </div>
              ))
            )}
          </div>
          {drag ? (
            <div
              className="timeline-drag"
              style={{
                left: dragStart * pxPerSecond,
                width: Math.max(2, (dragEnd - dragStart) * pxPerSecond),
              }}
            >
              <span className="timeline-drag-label mono">
                {formatTime(dragStart)} → {formatTime(dragEnd)}
              </span>
            </div>
          ) : null}
          <div
            className="timeline-playhead"
            style={{ left: Math.min(contentWidth, currentTime * pxPerSecond) }}
          />
        </div>
      </div>
    </div>
  )
}
